import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { colors, customShadows, borderRadius, borderRadiusPx } from "../../../../theme";

/**
 * RevenueBreakdownCard - Displays revenue source items, icons, amounts, and percentage allocations.
 */
const RevenueBreakdownCard = ({
  title = "",
  items = [],
  totalLabel = "",
  totalAmount = "",
  totalPercentage = "",
  currencyPrefix = "",
  currencySuffix = "",
  sx = {},
}) => {
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        backgroundColor: colors.dashboard.cardBg,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1, display: "flex", flexDirection: "column" }}>
        {title && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              color: colors.dashboard.title,
              mb: 2,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>
        )}

        {items && items.length > 0 && (
          <TableContainer sx={{ flex: 1 }}>
            <Table size="small" sx={{ "& td, & th": { border: 0, py: 1, px: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colors.slate[500], fontWeight: 600, fontSize: "0.78rem" }}>
                    Revenue Source
                  </TableCell>
                  <TableCell align="right" sx={{ color: colors.slate[500], fontWeight: 600, fontSize: "0.78rem" }}>
                    Amount
                  </TableCell>
                  <TableCell align="right" sx={{ color: colors.slate[500], fontWeight: 600, fontSize: "0.78rem" }}>
                    % of Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": { backgroundColor: colors.dashboard.tableRowHover },
                      transition: "background-color 0.15s",
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        {item.icon && (
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: borderRadiusPx.md,
                              backgroundColor: item.iconBg || colors.blue[50],
                              color: item.iconColor || colors.blue[600],
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.85rem",
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.82rem",
                            color: colors.slate[700],
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.82rem",
                          color: colors.slate[900],
                        }}
                      >
                        {currencyPrefix}
                        {item.amount}
                        {currencySuffix}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.82rem",
                          color: colors.slate[500],
                        }}
                      >
                        {item.percentage}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {(totalAmount !== "" || totalLabel !== "") && (
          <>
            <Divider sx={{ my: 1.5, borderColor: colors.dashboard.cardBorder }} />

            {/* Total Summary Row */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ pt: 0.5 }}
            >
              {totalLabel && (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, fontSize: "0.84rem", color: colors.slate[900] }}
                >
                  {totalLabel}
                </Typography>
              )}
              <Stack direction="row" spacing={2} alignItems="center">
                {totalAmount !== "" && (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, fontSize: "0.88rem", color: colors.slate[900] }}
                  >
                    {currencyPrefix}
                    {totalAmount}
                    {currencySuffix}
                  </Typography>
                )}
                {totalPercentage !== "" && (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, fontSize: "0.84rem", color: colors.slate[900] }}
                  >
                    {totalPercentage}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

RevenueBreakdownCard.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node,
      iconBg: PropTypes.string,
      iconColor: PropTypes.string,
      label: PropTypes.string.isRequired,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      percentage: PropTypes.string.isRequired,
    })
  ),
  totalLabel: PropTypes.string,
  totalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  totalPercentage: PropTypes.string,
  currencyPrefix: PropTypes.string,
  currencySuffix: PropTypes.string,
  sx: PropTypes.object,
};

export default RevenueBreakdownCard;
